'use client';

import { createElement } from 'react';
import { CheckCircle, ChevronDown, Loader2, Settings, XCircle } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useLocale, type CustomI18n } from '@instamenu/context-i18n';
import { useDisclosure } from '@instamenu/react-hooks';
import * as styles from './function-call.css';
import { clsx } from 'clsx';
import type { ChatHistoryItem } from '../types';

export type FunctionCallStatus = 'processing' | 'ready' | 'completed' | 'error';

export type FunctionCallProps = {
  item: ChatHistoryItem & { type: 'function_call' };
  defaultOpen?: boolean;
  className?: string;
};

const getFunctionCallStatus = (
  item: ChatHistoryItem & { type: 'function_call' },
): FunctionCallStatus => {
  const status = item.role === 'assistant' ? item.status : undefined;

  if (
    item.sqlRunState &&
    !['succeeded', 'failed', 'canceled', 'submitted'].includes(item.sqlRunState)
  )
    return 'processing';
  if (item.sqlRunState === 'failed' || item.sqlRunState === 'canceled') return 'error';
  if (status === 'completed' && item.output) return 'completed';
  if (status === 'failed' || item.errorText) return 'error';
  if (item.arguments) return 'ready';
  return 'processing';
};

const stateIcons = {
  processing: Loader2,
  ready: Settings,
  completed: CheckCircle,
  error: XCircle,
} as const;

const getStateIcon = (state: FunctionCallStatus) => {
  return createElement(stateIcons[state], { className: styles.functionCallIcon({ state }) });
};

const getStateBadgeText = (state: FunctionCallStatus, i18n: CustomI18n) => {
  return i18n.t(`aiAssistant.functionCallStatus.${state}`);
};

const formatValue = (value: unknown): string => {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (typeof value === 'string') return value;
  return JSON.stringify(value, null, 2);
};

const parseJSON = (json?: string) => {
  if (!json) return undefined;
  try {
    return JSON.parse(json);
  } catch {
    return undefined;
  }
};

export const FunctionCall = observer((props: FunctionCallProps) => {
  const { item, defaultOpen = false, className } = props;
  const [isOpen, { toggle }] = useDisclosure(defaultOpen);
  const { i18n } = useLocale();

  const state = getFunctionCallStatus(item);
  const input = parseJSON(item.arguments);
  const output = parseJSON(item.output);

  return (
    <div className={clsx(styles.functionCallContainer, className)}>
      <button className={styles.functionCallHeader} onClick={toggle} type="button">
        <div className={styles.functionCallHeaderContent}>
          <div className={styles.functionCallHeaderLeft}>
            {getStateIcon(state)}
            <span className={styles.functionCallName}>
              {i18n.t(`aiAssistant.functionCalls.${item.name}`)}
            </span>
            <span className={styles.stateBadge({ state })}>
              {item.sqlRunState
                ? i18n.t(`aiAssistant.sqlRunStates.${item.sqlRunState}`)
                : getStateBadgeText(state, i18n)}
            </span>
          </div>
          <ChevronDown className={styles.chevronIcon({ isOpen })} />
        </div>
      </button>

      {isOpen && (
        <div className={styles.functionCallContent}>
          {input && Object.keys(input).length > 0 && (
            <div className={styles.functionCallSection}>
              <h4 className={styles.functionCallSectionTitle}>Input</h4>
              <div className={styles.functionCallCodeBlock}>
                {Object.entries(input).map(([key, value]) => (
                  <div key={key} className={styles.functionCallCodeLine}>
                    <span className={styles.functionCallCodeKey}>{key}:</span>
                    <span className={styles.functionCallCodeValue}>{formatValue(value)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {output && (
            <div className={styles.functionCallSection}>
              <h4 className={styles.functionCallSectionTitle}>Output</h4>
              <div className={styles.functionCallCodeBlock}>
                <pre className={styles.functionCallOutputPre}>{formatValue(output)}</pre>
              </div>
            </div>
          )}

          {state === 'error' && item.errorText && (
            <div className={styles.functionCallSection}>
              <h4 className={clsx(styles.functionCallSectionTitle, styles.functionCallErrorBlock)}>
                Error
              </h4>
              <div className={clsx(styles.functionCallCodeBlock, styles.functionCallErrorBlock)}>
                {item.errorText}
              </div>
            </div>
          )}

          {state === 'processing' && (
            <div className={styles.functionCallLoadingText}>Processing function call...</div>
          )}
        </div>
      )}
    </div>
  );
});
